import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextArea } from '../ui/TextArea';
import { IconButton } from '../ui/IconButton';
import { 
  FeatherUser,
  FeatherSave,
  FeatherEdit2,
  FeatherTrash2,
  FeatherX,
  FeatherCheck,
  FeatherChevronLeft,
  FeatherChevronRight
} from '../ui/icons';
import api from '../../services/api';
import './NoteReplies.css';

const NoteReplies = ({ noteId, isSharedWithMe, currentUserId, noteOwnerId, compactMode = false }) => {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Determine if current user can reply (they are the recipient of the shared note)
  const canReply = isSharedWithMe && currentUserId && noteOwnerId && currentUserId !== noteOwnerId;

  useEffect(() => {
    if (noteId) {
      loadReplies();
    }
  }, [noteId]);

  const loadReplies = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/scribble/replies/${noteId}`);
      setReplies(response.data || []);
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!newReply.trim()) return;

    try {
      setSubmitting(true);
      const response = await api.post(`/api/scribble/replies/${noteId}`, {
        content: newReply.trim()
      });
      setReplies(prev => [...prev, response.data]);
      setNewReply('');
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error creating reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditReply = async (replyId) => {
    if (!editingContent.trim()) return;

    try {
      setSubmitting(true);
      const response = await api.put(`/api/scribble/replies/${replyId}`, {
        content: editingContent.trim()
      });
      setReplies(prev => prev.map(reply => 
        reply.id === replyId ? response.data : reply
      ));
      setEditingReplyId(null);
      setEditingContent('');
    } catch (error) {
      console.error('Error updating reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }

    try {
      await api.delete(`/api/scribble/replies/${replyId}`);
      setReplies(prev => prev.filter(reply => reply.id !== replyId));
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const startEditing = (reply) => {
    setEditingReplyId(reply.id);
    setEditingContent(reply.content);
  };

  const cancelEditing = () => {
    setEditingReplyId(null);
    setEditingContent('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="replies-loading">Loading replies...</div>;
  }

  // Compact mode for dashboard - show only reply count and latest reply
  if (compactMode) {
    if (replies.length === 0) {
      return null; // Don't show anything if no replies
    }
    
    const latestReply = replies[replies.length - 1];
    return (
      <div className="note-replies-compact">
        <div 
          className="replies-summary clickable"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <span className="replies-count">
            💬 {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          </span>
          {replies.length > 1 && (
            <span className="replies-more">
              +{replies.length - 1} more
            </span>
          )}
          <span className="collapse-icon">
            {isCollapsed ? <FeatherChevronRight size={14} /> : <FeatherChevronLeft size={14} />}
          </span>
        </div>
        {!isCollapsed && (
          <div className="latest-reply-preview">
            <span className="latest-author">
              {latestReply.user?.display_name || latestReply.user?.email || 'Unknown'}
            </span>
            <span className="latest-content">
              {latestReply.content.length > 50 
                ? `${latestReply.content.substring(0, 50)}...` 
                : latestReply.content
              }
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="note-replies">
      <div className="replies-header">
        <div 
          className="replies-title clickable"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <FeatherUser size={16} />
          <span>Replies ({replies.length})</span>
          <span className="collapse-icon">
            {isCollapsed ? <FeatherChevronRight size={16} /> : <FeatherChevronLeft size={16} />}
          </span>
        </div>
        {canReply && !isCollapsed && (
          <Button
            variant="neutral-tertiary"
            size="small"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            {showReplyForm ? 'Cancel' : 'Add Reply'}
          </Button>
        )}
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* Reply Form */}
          {showReplyForm && canReply && (
            <div className="reply-form">
              <TextArea label="Your Reply">
                <TextArea.Input
                  value={newReply}
                  onChange={(e) => setNewReply(e.target.value)}
                  placeholder="Write your reply..."
                  rows={3}
                />
              </TextArea>
              <div className="reply-form-actions">
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={() => setShowReplyForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  size="small"
                  onClick={handleSubmitReply}
                  disabled={submitting || !newReply.trim()}
                >
                  <FeatherSave size={14} />
                  {submitting ? 'Sending...' : 'Send Reply'}
                </Button>
              </div>
            </div>
          )}

          {/* Replies List */}
          <div className="replies-list">
        {replies.length === 0 ? (
          <div className="no-replies">
            {canReply ? 'No replies yet. Be the first to reply!' : 'No replies yet.'}
          </div>
        ) : (
          replies.map(reply => (
            <div key={reply.id} className="reply-item">
              <div className="reply-header">
                <div className="reply-author">
                  <span className="author-name">
                    {reply.user?.display_name || reply.user?.email || 'Unknown User'}
                  </span>
                  <span className="reply-date">
                    {formatDate(reply.created_at)}
                  </span>
                </div>
                {reply.user_id === currentUserId && (
                  <div className="reply-actions">
                    {editingReplyId === reply.id ? (
                      <>
                        <IconButton
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherCheck />}
                          onClick={() => handleEditReply(reply.id)}
                          disabled={submitting}
                        />
                        <IconButton
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherX />}
                          onClick={cancelEditing}
                          disabled={submitting}
                        />
                      </>
                    ) : (
                      <>
                        <IconButton
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherEdit2 />}
                          onClick={() => startEditing(reply)}
                        />
                        <IconButton
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherTrash2 />}
                          onClick={() => handleDeleteReply(reply.id)}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
              
              <div className="reply-content">
                {editingReplyId === reply.id ? (
                  <TextArea>
                    <TextArea.Input
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      rows={2}
                    />
                  </TextArea>
                ) : (
                  <p>{reply.content}</p>
                )}
              </div>
            </div>
          ))
        )}
          </div>
        </>
      )}
    </div>
  );
};

export default NoteReplies; 